import { faArrowDown, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useContext, useMemo, useState } from 'react';

import AppContext from '@/contexts/appContext';
import { invalidateCycleQueries } from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import { scheduleExpectationRepository } from '@/repositories/ScheduleExpectationRepository';
import { Alert, Button, Modal, RadioGroup } from '@/ui';
import {
  addDaysToDateString,
  formatWeekdayCalendarDate,
  getNextMonday,
  getThisWeeksMonday,
  replaceTimezone
} from '@/utils/dates';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { Props } from './types';

const NULL_EXPECTATION = { cycle_time: '4', frequency: '0', look_ahead_cycles: '0' } as const;

// fallback_default_expectation can be present but still be a zero-frequency
// default, which doesn't count as something to protect against being
// re-exposed.
const hasActionableFallback = (
  expectation: { fallback_default_expectation?: { frequency: number } | null } | null | undefined
) => (expectation?.fallback_default_expectation?.frequency ?? 0) > 0;

const SwitchAppointmentDurationModal = ({
  appointmentType,
  onClose,
  open,
  phase,
  timeZone
}: Props) => {
  const { addMessage, host } = useContext(AppContext);
  const queryClient = useQueryClient();
  const counterpart = appointmentType.counterpart_appointment_type;
  const originating = appointmentType.current_expectation;
  const phaseId = phase.id;

  // AP-8903 restricts the switch to these four Mondays: last week's, the
  // current week's (which can already be underway), the upcoming one, and
  // the one after that -- not an open-ended date picker.
  const referenceDate = useMemo(() => replaceTimezone(new Date(), timeZone), [timeZone]);
  const thisWeeksMonday = useMemo(() => getThisWeeksMonday(referenceDate), [referenceDate]);
  const lastMonday = useMemo(() => addDaysToDateString(thisWeeksMonday, -7), [thisWeeksMonday]);
  const upcomingMonday = useMemo(() => getNextMonday(referenceDate), [referenceDate]);
  const followingMonday = useMemo(() => addDaysToDateString(upcomingMonday, 7), [upcomingMonday]);
  const mondayOptions = useMemo(
    () => [
      { value: lastMonday, label: `Last week · ${formatWeekdayCalendarDate(lastMonday)}` },
      {
        value: thisWeeksMonday,
        label: `This week · ${formatWeekdayCalendarDate(thisWeeksMonday)}`
      },
      { value: upcomingMonday, label: `Next week · ${formatWeekdayCalendarDate(upcomingMonday)}` },
      {
        value: followingMonday,
        label: `In 2 weeks · ${formatWeekdayCalendarDate(followingMonday)}`
      }
    ],
    [lastMonday, thisWeeksMonday, upcomingMonday, followingMonday]
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(upcomingMonday);
  const [submitted, setSubmitted] = useState(false);

  const hasBookedAppointmentsFromDate = useMemo(() => {
    if (!selectedDate) return false;
    const selected = new Date(`${selectedDate}T00:00:00`);
    return appointmentType.schedule_expectation_cycles.some((cycle) =>
      (cycle.appointment_attendances ?? []).some(
        (attendance) => !attendance.cancelled && new Date(attendance.start_at) >= selected
      )
    );
  }, [appointmentType.schedule_expectation_cycles, selectedDate]);

  const switchDuration = useMutation({
    mutationFn: async (date: string) => {
      if (!counterpart || !originating) {
        throw new Error('Unable to determine the current expectation');
      }
      const base = { phase: phaseId, note: null, max_attendances: null, start_at: date } as const;

      if (originating.custom_schedule_expectation_id) {
        await scheduleExpectationRepository.updateCustomScheduleExpectation({
          host,
          id: originating.custom_schedule_expectation_id,
          data: { end_at: date }
        });
        if (hasActionableFallback(originating)) {
          // Ending the custom reveals an actionable default underneath it, so
          // turn that back off too with a null custom of our own.
          await scheduleExpectationRepository.createCustomScheduleExpectation({
            host,
            data: {
              ...base,
              ...NULL_EXPECTATION,
              appointment_type: appointmentType.appointment_type_id,
              time_unit: originating.time_unit
            }
          });
        }
      } else {
        await scheduleExpectationRepository.createCustomScheduleExpectation({
          host,
          data: {
            ...base,
            ...NULL_EXPECTATION,
            appointment_type: appointmentType.appointment_type_id,
            time_unit: originating.time_unit
          }
        });
      }

      const counterpartExpectation = counterpart.current_expectation;
      const isNullCustom = !!counterpartExpectation?.custom_schedule_expectation_id;
      if (isNullCustom) {
        await scheduleExpectationRepository.updateCustomScheduleExpectation({
          host,
          id: counterpartExpectation!.custom_schedule_expectation_id as string,
          data: { end_at: date }
        });
      }
      if (!isNullCustom || !hasActionableFallback(counterpartExpectation)) {
        // Create an actionable custom unless ending the null custom above
        // already revealed one.
        await scheduleExpectationRepository.createCustomScheduleExpectation({
          host,
          data: {
            ...base,
            appointment_type: counterpart.appointment_type_id,
            cycle_time: String(originating.cycle_time),
            frequency: String(originating.frequency),
            look_ahead_cycles: String(originating.look_ahead_cycles),
            max_attendances: originating.max_attendances,
            time_unit: originating.time_unit
          }
        });
      }
    },
    onSuccess: async () => {
      addMessage({
        level: 'success',
        text: `Switched to ${counterpart?.appointment_type_name}`
      });
      await invalidateCycleQueries(queryClient, phaseId);
      setSubmitted(true);
    },
    onError: async (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
      // A partial failure may have changed some expectations.
      await invalidateCycleQueries(queryClient, phaseId);
    }
  });

  if (!counterpart) return null;

  const endsAt = selectedDate ? addDaysToDateString(selectedDate, -1) : null;

  return (
    <Modal
      dataTestId="switch-appointment-duration-modal"
      open={open}
      onClose={onClose}
      size="xl"
      title={
        submitted
          ? undefined
          : `Change ${appointmentType.appointment_type_name} to ${counterpart.appointment_type_duration} min`
      }
      primaryAction={
        submitted
          ? undefined
          : {
              label: selectedDate
                ? `Schedule change for ${formatWeekdayCalendarDate(selectedDate)}`
                : 'Schedule change',
              onClick: () => selectedDate && switchDuration.mutate(selectedDate),
              isLoading: switchDuration.isPending,
              disabled: !selectedDate || !originating || switchDuration.isPending
            }
      }
      secondaryAction={submitted ? undefined : { label: 'Cancel', onClick: onClose }}>
      {submitted ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500" />
          <div className="text-base font-semibold">Change scheduled</div>
          <p className="max-w-md text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
            {counterpart.appointment_type_name} starts{' '}
            {selectedDate && formatWeekdayCalendarDate(selectedDate)}. The{' '}
            {appointmentType.appointment_type_name} expectation ends{' '}
            {endsAt && formatWeekdayCalendarDate(endsAt)}.
          </p>
          <Button variant="default" color="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      ) : (
        <>
          <p className="mb-4 text-sm">
            Pick the Monday the {counterpart.appointment_type_duration}-minute expectation should
            start. The current {appointmentType.appointment_type_duration}-minute expectation ends
            the Sunday before, so each week is covered by exactly one of them. Weeks before that
            date don&rsquo;t change.
          </p>
          <div className="flex flex-wrap items-start gap-7">
            <div className="w-full shrink-0 sm:w-[280px]">
              <RadioGroup
                id="switch-duration-date"
                label="Switch on"
                onChange={(value) => setSelectedDate(value as string)}
                options={mondayOptions}
                optionsClassName="flex-col"
                selectedValue={selectedDate ?? ''}
              />
            </div>

            <div className="flex min-w-[220px] flex-1 flex-col gap-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                What will happen
              </div>

              <div className="rounded-lg border border-uniform-gray-200 p-3.5 dark:border-uniform-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {appointmentType.appointment_type_name}
                  </span>
                  <span className="text-xs text-uniform-gray-500 dark:text-uniform-gray-400">
                    Current
                  </span>
                </div>
                <div className="mt-1 text-sm">
                  Ends <strong>{endsAt ? formatWeekdayCalendarDate(endsAt) : '—'}</strong>
                </div>
              </div>

              <div className="flex justify-center text-uniform-gray-400 dark:text-uniform-gray-500">
                <FontAwesomeIcon icon={faArrowDown} />
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3.5 dark:border-blue-900 dark:bg-blue-950/30">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{counterpart.appointment_type_name}</span>
                  <span className="text-xs text-blue-700 dark:text-blue-300">New</span>
                </div>
                <div className="mt-1 text-sm">
                  Starts{' '}
                  <strong>
                    {selectedDate ? formatWeekdayCalendarDate(selectedDate) : 'Pick a Monday'}
                  </strong>
                </div>
                {originating && (
                  <div className="mt-1 text-xs text-uniform-gray-600 dark:text-uniform-gray-400">
                    Same cadence: {originating.frequency} per {originating.time_unit}
                  </div>
                )}
              </div>

              {selectedDate && hasBookedAppointmentsFromDate && (
                <Alert variant="warning">
                  {appointmentType.appointment_type_duration} min appointments already booked from{' '}
                  {formatWeekdayCalendarDate(selectedDate)} onward stay as they are. Rebook them if
                  they should be {counterpart.appointment_type_duration} min.
                </Alert>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default memo(SwitchAppointmentDurationModal);
